// MARCA | 28/09/2026 | The leftmost video autoplays muted until paused. Any visible video can play in place.
// Video hydration follows the shoppable media slider: sources stay in a template until the slide is needed.
import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [dots]
 *
 * @extends {Component<Refs>}
 */
class VideoTestimonialsComponent extends Component {
  /** @type {number} */
  #originalCount = 0;

  /** @type {number} */
  #prependCount = 2;

  /** @type {boolean} */
  #infinite = false;

  /** @type {boolean} */
  #reducedMotion = false;

  /** @type {boolean} */
  #isMuted = true;

  /** @type {boolean} */
  #inView = false;

  /** @type {boolean} */
  #isJumping = false;

  /** @type {boolean} */
  #ready = false;

  /** @type {boolean} */
  #dragging = false;

  /** @type {boolean} */
  #dragMoved = false;

  /** @type {number} */
  #dragStartX = 0;

  /** @type {number} */
  #dragScroll = 0;

  /** @type {HTMLElement | null} */
  #activeSlide = null;

  /** Original index of the leftmost slide when the visitor has paused it. */
  /** @type {number | null} */
  #pausedActiveIndex = null;

  /** Original indexes the visitor started, kept while that video stays on screen. */
  /** @type {Set<number>} */
  #manualPlayIndexes = new Set();

  /** @type {boolean} */
  #trackingPointer = false;

  /** @type {boolean} */
  #pointerMoved = false;

  /** @type {IntersectionObserver | null} */
  #sectionObserver = null;

  /** @type {AbortController} */
  #abort = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const originals = this.#slides().filter((slide) => slide.dataset.clone !== 'true');
    this.#originalCount = originals.length;
    if (!this.#originalCount) return;

    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.#reducedMotion) this.dataset.reducedMotion = 'true';

    const shopify = /** @type {{ designMode?: boolean } | undefined} */ (
      /** @type {any} */ (window).Shopify
    );
    const designMode = shopify?.designMode === true;
    this.#infinite = this.dataset.infinite === 'true' && this.#originalCount > 1 && !designMode;

    const scroller = this.refs.scroller;
    if (this.#infinite && scroller) {
      scroller.style.visibility = 'hidden';
      this.#buildInfinite(originals);
    }

    this.#hydrateVideos();
    this.#bind();
    this.#observeSection();

    requestAnimationFrame(() => {
      if (this.#infinite && scroller) {
        this.#scrollToOriginal(0, { smooth: false, preferMiddle: true });
      }

      requestAnimationFrame(() => {
        if (scroller) scroller.style.visibility = '';
        this.#updateActiveSlide();
        this.#ready = true;
      });
    });
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#sectionObserver?.disconnect();
    this.#slides().forEach((slide) => slide.querySelector('video')?.pause());
    super.disconnectedCallback();
  }

  /**
   * @param {HTMLElement[]} originals
   */
  #buildInfinite(originals) {
    const scroller = this.refs.scroller;
    if (!scroller) return;

    const makeClone = (/** @type {HTMLElement} */ source) => {
      const clone = /** @type {HTMLElement} */ (source.cloneNode(true));
      clone.removeAttribute('ref');
      [...clone.attributes].forEach((attr) => {
        if (attr.name.startsWith('data-shopify-')) clone.removeAttribute(attr.name);
      });
      clone.setAttribute('data-clone', 'true');
      return clone;
    };

    const before = document.createDocumentFragment();
    for (let set = 0; set < this.#prependCount; set++) {
      originals.forEach((slide) => before.appendChild(makeClone(slide)));
    }
    scroller.insertBefore(before, scroller.firstChild);

    for (let set = 0; set < this.#prependCount; set++) {
      originals.forEach((slide) => scroller.appendChild(makeClone(slide)));
    }
  }

  /**
   * Move each deferred video into the slide and keep it paused until it is the active one.
   */
  #hydrateVideos() {
    this.querySelectorAll('.video-testimonials__video-template').forEach((node) => {
      if (!(node instanceof HTMLTemplateElement)) return;
      const parent = node.parentElement;
      const sourceVideo = node.content.querySelector('video');
      if (!parent || !(sourceVideo instanceof HTMLVideoElement)) {
        node.remove();
        return;
      }

      const video = /** @type {HTMLVideoElement} */ (sourceVideo.cloneNode(true));
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.loop = true;
      video.autoplay = false;
      video.controls = false;
      video.pause();

      const poster = parent.querySelector('.video-testimonials__poster');
      if (poster instanceof HTMLImageElement && (poster.currentSrc || poster.src)) {
        video.poster = poster.currentSrc || poster.src;
      }

      parent.insertBefore(video, node);
      node.remove();
    });
  }

  #bind() {
    const signal = this.#abort.signal;
    const scroller = this.refs.scroller;
    if (!scroller) return;

    scroller.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    scroller.addEventListener('keydown', this.#onKeydown, { signal });
    scroller.addEventListener('pointerdown', this.#onPointerDown, { signal });
    scroller.addEventListener('pointermove', this.#onPointerMove, { signal });
    scroller.addEventListener('pointerup', this.#onPointerUp, { signal });
    scroller.addEventListener('pointercancel', this.#onPointerUp, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    window.addEventListener('resize', this.#onResize, { signal });
  }

  #observeSection() {
    this.#sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.#inView = entry.isIntersecting;
          if (!this.#inView) {
            this.#slides().forEach((slide) => {
              slide.querySelector('video')?.pause();
              slide.classList.remove('is-playing');
            });
            this.#syncPlayButtons();
            return;
          }
          this.#syncPlayback();
        });
      },
      { threshold: 0.2 }
    );

    this.#sectionObserver.observe(this);
  }

  /**
   * @returns {HTMLElement[]}
   */
  #slides() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.video-testimonials__slide')]);
  }

  // MARCA | 28/09/2026 | Snap line includes the mobile gutter inset so slides line up after the scroller runs to the screen edge.
  /**
   * Distance from the scroller's left edge to where a slide should snap.
   * On mobile this is the page gutter, so the full-bleed track still lines up.
   */
  #snapOffset() {
    const scroller = this.refs.scroller;
    if (!scroller) return 0;
    return parseFloat(getComputedStyle(scroller).paddingInlineStart) || 0;
  }

  /**
   * Content-position of a slide's left edge inside the scroller, aligned to the snap inset.
   * @param {HTMLElement} slide
   */
  #slideScrollLeft(slide) {
    const scroller = this.refs.scroller;
    if (!scroller) return 0;
    const scrollerRect = scroller.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    return scroller.scrollLeft + (slideRect.left - scrollerRect.left) - this.#snapOffset();
  }

  #onScroll = () => {
    if (!this.#ready || this.#isJumping) return;
    this.#queueSettle();
  };

  #queueSettle = debounce(() => {
    if (this.#isJumping) return;
    this.#maybeTeleport();
    this.#updateActiveSlide();
  }, 80);

  #onResize = debounce(() => {
    if (!this.#infinite) return;
    const index = Number(this.#activeSlide?.dataset.originalIndex ?? 0);
    this.#scrollToOriginal(index, { smooth: false, preferMiddle: true });
  }, 150);

  /**
   * When scrolling settles on a cloned slide, jump to the same slide in the
   * middle set so the loop can continue in either direction.
   */
  #maybeTeleport() {
    const scroller = this.refs.scroller;
    if (!scroller || !this.#infinite) return;

    const active = this.#closestSlide();
    if (!active) return;

    const index = Number(active.dataset.originalIndex);
    const middle = this.#slides()[this.#originalCount * this.#prependCount + index];
    if (!middle || middle === active) return;

    this.#jump(this.#slideScrollLeft(middle));
  }

  /**
   * @param {number} left
   */
  #jump(left) {
    const scroller = this.refs.scroller;
    if (!scroller) return;

    this.#isJumping = true;
    scroller.style.scrollSnapType = 'none';
    scroller.style.scrollBehavior = 'auto';
    scroller.scrollLeft = left;

    requestAnimationFrame(() => {
      scroller.style.scrollSnapType = '';
      scroller.scrollLeft = left;
      requestAnimationFrame(() => {
        scroller.style.scrollBehavior = '';
        this.#isJumping = false;
        this.#updateActiveSlide();
      });
    });
  }

  /**
   * @param {number} originalIndex
   * @param {{ smooth?: boolean, preferMiddle?: boolean }} [options]
   */
  #scrollToOriginal(originalIndex, options = {}) {
    const scroller = this.refs.scroller;
    if (!scroller) return;

    const slides = this.#slides().filter(
      (slide) => Number(slide.dataset.originalIndex) === originalIndex
    );
    if (!slides.length) return;

    let target = slides[0];
    if (options.preferMiddle && this.#infinite) {
      target = this.#slides()[this.#originalCount * this.#prependCount + originalIndex] ?? target;
    } else if (slides.length > 1) {
      const current = scroller.scrollLeft;
      let bestDistance = Infinity;
      slides.forEach((slide) => {
        const distance = Math.abs(this.#slideScrollLeft(slide) - current);
        if (distance < bestDistance) {
          bestDistance = distance;
          target = slide;
        }
      });
    }

    if (!target) return;

    scroller.scrollTo({
      left: this.#slideScrollLeft(target),
      behavior: options.smooth ? 'smooth' : 'auto',
    });
  }

  /**
   * @returns {HTMLElement | null}
   */
  #closestSlide() {
    const scroller = this.refs.scroller;
    if (!scroller) return null;

    const edge = scroller.getBoundingClientRect().left + this.#snapOffset();
    let closest = /** @type {HTMLElement | null} */ (null);
    let closestDistance = Infinity;

    this.#slides().forEach((slide) => {
      const distance = Math.abs(slide.getBoundingClientRect().left - edge);
      if (distance < closestDistance) {
        closestDistance = distance;
        closest = slide;
      }
    });

    return closest;
  }

  #updateActiveSlide() {
    const next = this.#closestSlide();
    if (!next) return;

    const activeIndex = Number(next.dataset.originalIndex);
    if (this.#pausedActiveIndex !== null && this.#pausedActiveIndex !== activeIndex) {
      this.#pausedActiveIndex = null;
    }

    this.#activeSlide = next;

    this.#slides().forEach((slide) => {
      slide.classList.toggle('is-active', slide === next);
    });

    this.querySelectorAll('.video-testimonials__dot').forEach((dot) => {
      if (!(dot instanceof HTMLButtonElement)) return;
      const isCurrent = Number(dot.dataset.slideIndex) === activeIndex;
      if (isCurrent) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    this.#syncPlayback();
  }

  /**
   * True when enough of the slide is inside the scroller for its control to be usable.
   * @param {HTMLElement} slide
   */
  #isSlideVisible(slide) {
    const scroller = this.refs.scroller;
    if (!scroller) return false;

    const scrollerRect = scroller.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    const visibleWidth =
      Math.min(slideRect.right, scrollerRect.right) - Math.max(slideRect.left, scrollerRect.left);

    return visibleWidth > slideRect.width * 0.5;
  }

  #syncPlayback() {
    const active = this.#activeSlide;
    /** @type {Set<number>} */
    const visibleIndexes = new Set();

    this.#slides().forEach((slide) => {
      const video = slide.querySelector('video');
      if (!(video instanceof HTMLVideoElement)) return;

      const index = Number(slide.dataset.originalIndex);
      const visible = this.#isSlideVisible(slide);
      if (visible) visibleIndexes.add(index);

      const isActive = slide === active;
      const pausedByUser = isActive && this.#pausedActiveIndex === index;
      const manual = this.#manualPlayIndexes.has(index) && visible;
      const autoplay = isActive && !this.#reducedMotion && this.#pausedActiveIndex !== index;
      const shouldPlay = this.#inView && visible && !pausedByUser && (manual || autoplay);

      video.muted = this.#isMuted;

      if (shouldPlay) {
        video.play().catch(() => {
          slide.classList.remove('is-playing');
        });
        slide.classList.add('is-playing');
      } else {
        video.pause();
        slide.classList.remove('is-playing');
        if (!visible) video.currentTime = 0;
      }
    });

    this.#manualPlayIndexes.forEach((index) => {
      if (!visibleIndexes.has(index)) this.#manualPlayIndexes.delete(index);
    });

    this.#syncSoundButtons();
    this.#syncPlayButtons();
  }

  #syncPlayButtons() {
    this.#slides().forEach((slide) => {
      const visible = this.#isSlideVisible(slide);
      const playing = slide.classList.contains('is-playing');
      const isActive = slide === this.#activeSlide;

      slide.querySelectorAll('button').forEach((button) => {
        if (!(button instanceof HTMLButtonElement)) return;

        const action = button.dataset.action;
        const show =
          visible && (action === 'toggle-play' || (action === 'toggle-sound' && (isActive || playing)));

        button.tabIndex = show ? 0 : -1;
        if (show) button.removeAttribute('aria-hidden');
        else button.setAttribute('aria-hidden', 'true');

        if (action !== 'toggle-play') return;

        const label = playing ? button.dataset.pauseLabel : button.dataset.playLabel;
        if (label) button.setAttribute('aria-label', label);
        button.setAttribute('aria-pressed', playing ? 'true' : 'false');
      });
    });
  }

  #syncSoundButtons() {
    this.querySelectorAll('.video-testimonials__sound').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      const label = this.#isMuted ? button.dataset.mutedLabel : button.dataset.unmutedLabel;
      button.dataset.muted = this.#isMuted ? 'true' : 'false';
      button.setAttribute('aria-pressed', this.#isMuted ? 'false' : 'true');
      if (label) button.setAttribute('aria-label', label);
    });
  }

  /**
   * @param {Event} event
   */
  #onClick = (event) => {
    if (this.#dragMoved || this.#pointerMoved) {
      this.#dragMoved = false;
      this.#pointerMoved = false;
      return;
    }

    const target = event.target;
    if (!(target instanceof Element)) return;

    const dot = target.closest('[data-slide-index]');
    if (dot instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#scrollToOriginal(Number(dot.dataset.slideIndex), {
        smooth: !this.#reducedMotion,
      });
      return;
    }

    const soundButton = target.closest('[data-action="toggle-sound"]');
    if (soundButton instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#toggleSound();
      return;
    }

    const playTarget = target.closest('[data-action="toggle-play"], .video-testimonials__media');
    if (playTarget) {
      const slide = playTarget.closest('.video-testimonials__slide');
      if (slide instanceof HTMLElement) {
        event.preventDefault();
        this.#toggleSlidePlayback(slide);
      }
    }
  };

  /**
   * Play or pause one slide without moving the carousel.
   * @param {HTMLElement} slide
   */
  #toggleSlidePlayback(slide) {
    const video = slide.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;

    const index = Number(slide.dataset.originalIndex);
    const playing = slide.classList.contains('is-playing') && !video.paused;

    if (playing) {
      this.#manualPlayIndexes.delete(index);
      if (slide === this.#activeSlide) this.#pausedActiveIndex = index;
    } else {
      this.#manualPlayIndexes.add(index);
      if (this.#pausedActiveIndex === index) this.#pausedActiveIndex = null;
    }

    this.#syncPlayback();
  }

  #toggleSound() {
    this.#isMuted = !this.#isMuted;
    this.#slides().forEach((other) => {
      const otherVideo = other.querySelector('video');
      if (otherVideo instanceof HTMLVideoElement) otherVideo.muted = this.#isMuted;
    });
    this.#syncSoundButtons();
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();

    const current = Number(this.#activeSlide?.dataset.originalIndex ?? 0);
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const next = this.#infinite
      ? (current + direction + this.#originalCount) % this.#originalCount
      : Math.max(0, Math.min(this.#originalCount - 1, current + direction));

    this.#scrollToOriginal(next, { smooth: !this.#reducedMotion });
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerDown = (event) => {
    this.#trackingPointer = true;
    this.#pointerMoved = false;
    this.#dragStartX = event.clientX;

    if (event.pointerType === 'touch' || event.button !== 0) return;
    if (event.target instanceof Element && event.target.closest('button')) return;

    const scroller = this.refs.scroller;
    if (!scroller) return;

    this.#dragging = true;
    this.#dragMoved = false;
    this.#dragStartX = event.clientX;
    this.#dragScroll = scroller.scrollLeft;
    scroller.classList.add('is-dragging');
    scroller.setPointerCapture(event.pointerId);
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    if (this.#trackingPointer && Math.abs(event.clientX - this.#dragStartX) > 8) {
      this.#pointerMoved = true;
    }
    if (!this.#dragging) return;
    const scroller = this.refs.scroller;
    if (!scroller) return;

    const delta = event.clientX - this.#dragStartX;
    if (Math.abs(delta) > 4) this.#dragMoved = true;
    scroller.scrollLeft = this.#dragScroll - delta;
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerUp = (event) => {
    this.#trackingPointer = false;
    if (!this.#dragging) return;
    const scroller = this.refs.scroller;
    this.#dragging = false;
    scroller?.classList.remove('is-dragging');

    if (scroller?.hasPointerCapture(event.pointerId)) {
      scroller.releasePointerCapture(event.pointerId);
    }

    if (this.#dragMoved) {
      setTimeout(() => {
        this.#dragMoved = false;
      }, 0);
    }

    if (!this.#dragMoved) return;

    const slide = this.#closestSlide();
    if (!slide || !scroller) return;

    scroller.scrollTo({
      left: this.#slideScrollLeft(slide),
      behavior: this.#reducedMotion ? 'auto' : 'smooth',
    });
  };
}

if (!customElements.get('video-testimonials-component')) {
  customElements.define('video-testimonials-component', VideoTestimonialsComponent);
}
